import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Modal } from '@core/enums/modal';
import { Permission } from '@core/enums/permissions';
import { SelectType } from '@core/enums/select';
import { TableColumnType } from '@core/enums/table';
import { IClassroomAssignment } from '@core/interfaces/iclasses';
import { AuthService } from '@core/services/auth.service';
import { ClassService } from '@core/services/class.service';
import { PermissionService } from '@core/services/permission.service';
import { RatingPeriodService } from '@core/services/rating-period.service';
import { RatingService } from '@core/services/rating.service';
import { ToastService } from '@core/services/toast.service';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { FilterSelectComponent } from '@shared/components/dropdowns/filter-select/filter-select.component';
import { take } from 'rxjs';
import { AssessmentDateModalComponent } from '../components/assessment-date-modal/assessment-date-modal.component';
import { AgencyService } from '@core/services/agency.service';
import { IAgency } from '@core/interfaces/iagency';
import { RatingViewVersionEnum } from '@core/enums/rating-view-version.enum';
import { StateService } from '@core/services/state.service';
import { SchoolYear } from '@core/enums/school-year';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-search-rating',
    templateUrl: './search-rating.component.html',
    styleUrls: ['./search-rating.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchRatingComponent implements OnInit {
  @Output() formValues = new EventEmitter();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  pageSizeOptions: number[] = [10, 25, 50, 100];
  pageSize: number = 10;
  totalData: number = 0;
  pageIndex: number = 0;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  tableColumns: any[] = [];
  tableData?: any;
  isAgeGradeRequired = true;

  agi2025Flags: Record<'agency' | 'site' | 'classroom' | 'ratingPeriod' | 'schoolYear' | 'final2025', boolean> = {
    agency: false,
    site: false,
    classroom: false,
    ratingPeriod: false,
    schoolYear: false,
    final2025: false
  };

  ratingViewVersion = RatingViewVersionEnum.VERSION_2015;

  get show2025Agi(): boolean {
    return ((this.agi2025Flags.agency || this.agi2025Flags.site || this.agi2025Flags.classroom) && this.agi2025Flags.schoolYear)
      || this.agi2025Flags.ratingPeriod || this.agi2025Flags.final2025;
  }


  @ViewChild('classroomSelect') classroomSelect:
    | FilterSelectComponent
    | undefined;
  @ViewChild('agencySelect') agencySelect: FilterSelectComponent | undefined;
  @ViewChild('sitesSelect') sitesSelect: FilterSelectComponent | undefined;
  @ViewChild('ratingPeriodSelect') ratingPeriodSelect:
    | FilterSelectComponent
    | undefined;
  @ViewChild('stateSelect') stateSelect: FilterSelectComponent | undefined;
  @ViewChild('ageGroupInstrumentSelect') ageGroupInstrumentSelect:
    | FilterSelectComponent
    | undefined;

  user = this.auth.getCurrentUser();
  initAgencyId: number | null = null;
  searchForm: FormGroup | any;
  classroomList: IClassroomAssignment[] = [];
  select = SelectType;
  clearDropdown = false;
  isSuperUser = this.permissionService.checkPermission(
    Permission.AccessAllAgencies
  );
  canLock = false;
  canUnlock = false;
  isAgencyLocked = true;
  classAssessmentDate: any = null;
  minRatingPeriodDate = new Date();
  maxRatingPeriodDate = new Date();
  minEnrollmentDate: Date = new Date();
  selectedSchoolYearId: number | null = null;

  canLockRatings = this.permissionService.checkPermission(
    Permission.LockRatings
  );

  canUnlockRatings = this.permissionService.checkPermission(
    Permission.UnlockRatings
  );


  get stateId() {
    return this.searchForm.get('stateId');
  }

  get agencyId() {
    return this.searchForm.get('agencyId');
  }
  get siteId() {
    return this.searchForm.get('siteId');
  }
  get ratingPeriodId() {
    return this.searchForm.get('ratingPeriodId');
  }
  get classId() {
    return this.searchForm.get('classId');
  }
  get ageGradeInstrumentId() {
    return this.searchForm.get('ageGradeInstrumentId');
  }

  constructor(
    private auth: AuthService,
    private ratingPeriodService: RatingPeriodService,
    private classService: ClassService,
    private toastService: ToastService,
    private fb: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private permissionService: PermissionService,
    private ratingService: RatingService,
    private agencyService: AgencyService,
    private route: ActivatedRoute,
    private stateService: StateService,
    private translocoService: TranslocoService
  ) { }

  checkStateConfiguration(): void {
    if (this.stateId?.value) {
      this.stateService.getStatesById(this.stateId.value).pipe(
        take(1)
      ).subscribe((state: any) => {
        this.ratingViewVersion = state.ratingViewVersionId ? state.ratingViewVersionId : RatingViewVersionEnum.VERSION_2015;
        this.checkAgeGradeRequired();
      });
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    if(this.paginator)
      this.paginator._intl.itemsPerPageLabel = '';
  }

  ngOnInit(): void {

    this.initializeForm();

    if (this.user.agencyId) {
      this.agencyService.getAgency(this.user.agencyId)
      .pipe(take(1))
      .subscribe((res: IAgency) => {
        this.agi2025Flags.agency = res?.isSoftImplementation ?? false;
        this.checkStateConfiguration();
      });
    }

    const classId = this.route.snapshot.paramMap.get('classId');
    const instrumentId = this.route.snapshot.paramMap.get('instrumentId');
    if (classId != null && instrumentId != null) {
      this.classService.getStudentsByClass(+classId).subscribe( res => {
        this.initAgencyId = res.classroom?.agencyId;
        this.handleState(res.classroom?.site?.stateId);
        this.handleChosenAgency(res.classroom?.agencyId);
        this.handleChosenRatingPeriod(res.ratingPeriod);
        this.handleChosenSite(res.siteId);
        this.handleChosenClassroom(res.id);
        this.agi2025Flags.classroom = res.classroom?.isSoftImplementation ?? false;
        this.handleAgeGroupInstrument(instrumentId);
        this.search()
      });
    } else {
      this.initAgencyId = this.user.agencyId;
    }
  }

  initializeForm(): void {
    this.searchForm = this.fb.group({
      stateId: [this.user.stateId ?? null, [Validators.required]],
      agencyId: [this.user.agencyId ?? null, [Validators.required]],
      ratingPeriodId: [null, [Validators.required]],
      siteId: [null, [Validators.required]],
      classId: [null, [Validators.required]],
      ageGradeInstrumentId: [null, [Validators.required]],
    });
  }

  handleState(state: any) {
    this.stateId.setValue(state instanceof Object ? state?.id : state);
    this.agencyId.setValue(null);
    this.ageGradeInstrumentId.setValue(null);
    this.classId.setValue(null);
    this.checkStateConfiguration();
    this.formValues.emit(this.searchForm.value);
  }

  handleChosenAgency(agency: any): void {
    const selectedAgencyId = agency instanceof Object ? agency?.id : agency;
    if (selectedAgencyId != this.agencyId.value) {
      this.agencyId.setValue(selectedAgencyId);
      this.classId.setValue(null);
      this.siteId.setValue(null);
      this.ratingPeriodId.setValue(null);
      this.agi2025Flags.site = false;
      this.agi2025Flags.classroom = false;
      this.agi2025Flags.ratingPeriod = false;
      this.classroomList = [];
      this.formValues.emit(this.searchForm.value);
    }
    this.agi2025Flags.agency = agency?.item?.isSoftImplementation ?? agency?.isSoftImplementation ?? false;
  }

  handleChosenSite(site: any): void {
    const selectedSiteId = site instanceof Object ? site?.id ?? site[0]?.id : site;
    if (selectedSiteId != this.siteId.value) {
      this.classId.setValue(null);      
      this.agi2025Flags.classroom = false;
    }

    this.siteId.setValue(selectedSiteId);
    this.agi2025Flags.site = site instanceof Object ? site?.item?.isSoftImplementation ?? site[0]?.isSoftImplementation ?? false : false;
    this.updateClassroomDropdown();
    this.formValues.emit(this.searchForm.value);
  }

  handleChosenClassroom(classroom: any): void {
    this.classId.setValue(classroom instanceof Object ? classroom?.id : classroom);
    this.agi2025Flags.classroom = classroom?.item?.isSoftImplementation ?? false;
    this.formValues.emit(this.searchForm.value);
  }

  handleAgeGroupInstrument(ageGroupInstrument: any): void {
    this.ageGradeInstrumentId.setValue(ageGroupInstrument instanceof Object ? ageGroupInstrument?.id : ageGroupInstrument);
  }

  handleChosenRatingPeriod(ratingPeriod: any): void {
    this.ratingPeriodId.setValue(ratingPeriod instanceof Object ? ratingPeriod?.id : ratingPeriod);
    this.updateClassroomDropdown();
    this.agi2025Flags.ratingPeriod = ratingPeriod?.globalRatingPeriod?.isPilotEnabled ?? false;
    this.agi2025Flags.classroom = false;
    const enforce2025StateConfig = (ratingPeriod?.schoolYear?.enforceStateConfig ?? false) && (this.ratingViewVersion === RatingViewVersionEnum.VERSION_2025);
    this.agi2025Flags.schoolYear = ratingPeriod?.isSchoolYearEarlyAdopter || enforce2025StateConfig;
    this.agi2025Flags.final2025 = enforce2025StateConfig;
    this.selectedSchoolYearId = ratingPeriod?.schoolYearId ?? null;
    this.checkAgeGradeRequired();
    this.formValues.emit(this.searchForm.value);
  }

  checkAgeGradeRequired(): void {
    this.isAgeGradeRequired = !(this.selectedSchoolYearId != null && this.selectedSchoolYearId >= SchoolYear._2026_27 && this.ratingViewVersion === RatingViewVersionEnum.VERSION_2025);
    if (this.isAgeGradeRequired) {
      this.ageGradeInstrumentId.setValidators([Validators.required]);
    } else {
      this.ageGradeInstrumentId.clearValidators();
    }
    this.ageGradeInstrumentId.updateValueAndValidity();
  }

  updateClassroomDropdown(): void {
    this.classroomList = [];
    const payload: any = {};
    payload.siteIds = this.siteId.value ?? null;
    payload.siteIds = this.siteId.value ? [this.siteId.value] : null;

    payload.ratingPeriodId = this.ratingPeriodId.value ?? '';
    payload.itemsPerPage = 10;
    payload.useClassId = true;

    if (payload.siteIds?.length > 0 && payload.ratingPeriodId > 0) {
      this.classService
        .getClassesBySitesAndRatingPeriod(payload)
        .pipe(take(1))
        .subscribe((res: any) => {
          this.classroomList = res.filter(
            (value: any, index: any, self: any) =>
              index === self.findIndex((t: any) => t.id === value.id)
          );
        });
    }
  }

  search() {
    if (this.searchForm.invalid) {
      this.toastService.error(this.translocoService.translate('user.inputRating.search.requiredFieldsToast'));
      return;
    }
    this.paginateRatings(true);
  }

  paginateRatings(resetPage?: boolean) {
    const payload = {
      classId: this.classId.value,
      agencyId: this.agencyId.value,
      ageGradeInstrumentId: this.ageGradeInstrumentId.value,
      pageIndex: resetPage ? 0 : this.paginator.pageIndex + 1,
      itemsPerPage: resetPage ? 10 : this.paginator.pageSize,
    };

    this.ratingService.getClassRatings(payload).subscribe((res: any) => {
      if (res) {
        if (resetPage) this.paginator.firstPage();
        this.ratingService.setStudentResultIds(res.allItemIds);
        this.checkLockAndUnlock(res.items);
        this.initializeTableData(res);
        if (res.totalData > 25) this.pageSizeOptions.push(res.totalData);
      }
    });
  }

  checkLockAndUnlock(data: any[]) {
    if (data.length > 0) {
      const firstRecord = data[0];
      this.canLock =
        data.every((d) => d.totalDomainCount == d.totalDomainRatings) &&
        data.some((d) => !d.isLocked);
      this.canUnlock = !this.canLock && data.every((d) => d.isLocked);
      this.isAgencyLocked = firstRecord.isRatingViewLocked;
      this.minRatingPeriodDate = new Date(firstRecord.minStartDate);
      this.maxRatingPeriodDate = new Date(firstRecord.maxEndDate);
      this.minEnrollmentDate = new Date(Math.min(...data.map(d => new Date(d.classEnrollmentDate).getTime())));

      if (this.canLock) {
        const withAssessmentDate = data.find((d) => d.assessmentDate);
        if (withAssessmentDate)
          this.classAssessmentDate = new Date(
            withAssessmentDate.assessmentDate
          );
      }
    }
  }

  initializeTableData(res: any): void {
    this.initializeTableColumns(res.items);
    this.dataSource = new MatTableDataSource(res.items);
    this.totalData = res.totalData;
    this.tableData = res.items;
  }

  initializeTableColumns(data: any[]) {
    let domainRows: any[] = data.length > 0 ? data[0].displayDomains : [];

    this.tableColumns = [
      {
        columnDef: 'enrollmentId',
        header: this.translocoService.translate('user.inputRating.search.columns.ratings'),
        icon: 'file-alt',
        type: TableColumnType.actionIcon,
        action: 'view-rating',
        colorClass: 'text-drdpblue-300',
        cursorClass: 'cursor-pointer',
      },
      {
        columnDef: 'studentName',
        header: this.translocoService.translate('user.inputRating.search.columns.childName'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'studentId',
        header: this.translocoService.translate('user.inputRating.search.columns.childId'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'enrollmentType',
        header: this.translocoService.translate('user.inputRating.search.columns.enrollment'),
        type: TableColumnType.text,
        isSortable: true
      },
    ];

    domainRows.forEach((domain: string) => {
      const measureCode = domain
        .replace('-', '')
        .replace(':', '')
        .toLowerCase();
      this.tableColumns.push({
        columnDef: measureCode,
        // Domain names are server-supplied; the API localizes them, not Transloco.
        header: domain,
        type: TableColumnType.text,
      });
    });

    this.tableColumns.push(
      {
        columnDef: 'assessmentDate',
        header: this.translocoService.translate('user.inputRating.search.columns.assessmentDate'),
        type: TableColumnType.date,
        isSortable: true,
      },
      {
        columnDef: 'formattedLockingDate',
        header: this.translocoService.translate('user.inputRating.search.columns.lockDate'),
        type: TableColumnType.date,
        isSortable: true,
      },
      {
        columnDef: 'ratingCompletion',
        header: this.translocoService.translate('user.inputRating.search.columns.ratingCompletion'),
        type: TableColumnType.text,
      },
      {
        columnDef: 'isLocked',
        header: this.translocoService.translate('user.inputRating.search.columns.lockingStatus'),
        type: TableColumnType.boolIcon,
        truthyClass: 'text-drdpGreen-300',
        truthyIconUrl: 'assets/images/misc/lock-alt.svg',
        falsyClass: 'text-drdpgrey-200',
        falsyIconUrl: 'assets/images/misc/padlock.svg',
        isSortable: true,
      },
      {
        columnDef: 'teacherName',
        header: this.translocoService.translate('user.inputRating.search.columns.teacher'),
        type: TableColumnType.text,
        isSortable: true,
      },
      {
        columnDef: 'siteName',
        header: this.translocoService.translate('user.inputRating.search.columns.site'),
        type: TableColumnType.text,
        isSortable: true,
      }
    );
  }


  onIconClick(event: any): void {
    if (event.action == 'view-rating') {
      this.ratingService.setSearchContext(this.classId.value, this.ageGradeInstrumentId.value);
      this.router.navigate([`input-ratings/${event.data.enrollmentId}`]);
    }
  }

  clear(): void {
    this.classroomSelect?.clear();
    this.ratingPeriodSelect?.clear();
    this.clearDropdown = true;
    this.classroomList = [];
    this.selectedSchoolYearId = null;
    this.isAgeGradeRequired = true;

    if (!this.stateSelect?.disabled) {
      this.stateId.setValue(null);
    }

    if (!this.agencySelect?.disabled) {
      this.agencyId.setValue(null);
    }
  }

  lockClass() {
    if (!this.isAgencyLocked) {
      let event = { data: { modalInfo: {} } };
      event.data.modalInfo = {
        title: this.translocoService.translate('user.inputRating.common.errorTitle'),
        message: this.translocoService.translate('user.inputRating.common.agencyLockRequired'),
        primaryBtnClass: 'blue',
        primaryBtnText: Modal.OK,
      };
      this.dialog.open(ConfirmationModalComponent, {
        data: event,
      });
      return;
    }

    if (this.classId.value && this.stateId.value) {
      this.openAssessmentDateModal(this.classAssessmentDate,  this.minRatingPeriodDate, this.maxRatingPeriodDate, this.minEnrollmentDate, null, null,
        (result) => {
          const payload = {
            assessmentDate: result.assessmentDate,
            ageGradeInstrumentId: this.ageGradeInstrumentId.value,
            classId: this.classId.value,
            stateId: this.stateId.value
          };
          this.ratingService.lockClass(payload).pipe(take(1)).subscribe(() => {
            this.paginateRatings(true);
          });
        });
    }
  }

  openAssessmentDateModal(
    assessmentDate: Date,
    minDate: Date,
    maxDate: Date,
    enrollmentDate?: Date | null,
    customMessage?: string | null,
    title?: string | null,
    onCloseCallback?: (result: any) => void
  ) {
    const data = {
      assessmentDate,
      minDate,
      maxDate,
      enrollmentDate,
      customMessage,
      title,
    };

    const modalRef = this.dialog.open(AssessmentDateModalComponent, {
      data: data,
    });

    modalRef.afterClosed().subscribe((result: any) => {
      if (result && onCloseCallback) {
        onCloseCallback(result);
      }
    });
  }



  unlockClass() {
    let event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('user.inputRating.common.unlockTitle'),
      message: this.translocoService.translate('user.inputRating.common.unlockConfirm'),
      primaryBtnClass: 'blue',
      primaryBtnText: Modal.Confirm,
      showCancel: true,
    };
    const modalRef = this.dialog.open(ConfirmationModalComponent, {
      data: event,
    });
    modalRef.afterClosed().subscribe((res) => {
      if (res && this.classId.value) {
        const payload = {
          ageGradeInstrumentId: this.ageGradeInstrumentId.value,
          classId: this.classId.value,
        };

        this.ratingService
          .unlockClass(payload)
          .pipe(take(1))
          .subscribe((_) => {
            this.paginateRatings(true);
          });
      }
    });
  }
}
